import React, { useState } from "react";
import { Activity, ShieldOff, Crosshair, Wrench, ArrowUpRight, RefreshCw } from "lucide-react";

/**
 * Sovereign Match — fleet scouter that classifies a visitor's intent and routes
 * them to the correct CyberOps hull:
 *  - General IT / managed services / governance audit  → techops.eigooinc.net (Tech Ops)
 *  - Security specialist / red team / threat hunt      → techopssa.eigooinc.net (Tech Ops SA · Vault)
 *
 * Scoring is intentionally simple and explicit so Director can tune thresholds
 * without ML overhead. Two paths get rendered side-by-side so the visitor can
 * also bypass the questions entirely.
 */
const QUESTIONS = [
    {
        id: "scope",
        prompt: "What's the engagement at hand?",
        options: [
            { label: "Endpoint hygiene, IT support, day-2 ops", weight: { it: 2, sec: 0 } },
            { label: "Continuous compliance posture (SOC 2, ISO, NIST)", weight: { it: 1, sec: 1 } },
            { label: "Active threat hunt, red team, or incident response", weight: { it: 0, sec: 2 } },
        ],
    },
    {
        id: "stakeholder",
        prompt: "Who's asking for this work?",
        options: [
            { label: "Operations / IT manager", weight: { it: 2, sec: 0 } },
            { label: "Compliance / GRC lead", weight: { it: 1, sec: 1 } },
            { label: "CISO, board, or breach committee", weight: { it: 0, sec: 2 } },
        ],
    },
    {
        id: "urgency",
        prompt: "What's the urgency?",
        options: [
            { label: "Routine — quarterly cadence", weight: { it: 2, sec: 0 } },
            { label: "Pre-audit window, 30-60 days out", weight: { it: 1, sec: 1 } },
            { label: "Incident in progress — adversary suspected", weight: { it: 0, sec: 2 } },
        ],
    },
];

const RESULTS = {
    it: {
        title: "Tech Ops · General IT & Governance",
        copy: "You're scoping continuous attestation, day-2 IT ops, or compliance evidence work. The Tech Ops front door is calibrated for that cadence.",
        badge: "Standard front door",
        color: "hsl(153 55% 45%)",
        href: "https://techops.eigooinc.net",
        icon: Activity,
        ctaLabel: "Open Tech Ops",
    },
    sec: {
        title: "Tech Ops SA · The Vault",
        copy: "You're describing adversarial work — red team, threat hunting, or active IR. That's NDA-gated and lives in the Vault. Sovereign Match recommends Vault clearance.",
        badge: "NDA-gated · vetted operators",
        color: "hsl(40 75% 55%)",
        href: "https://techopssa.eigooinc.net",
        icon: ShieldOff,
        ctaLabel: "Enter the Vault",
    },
    tie: {
        title: "Both bridges in play",
        copy: "Your intent straddles both platforms. Start with Tech Ops for posture baselines; escalate to the Vault if the work shifts adversarial.",
        badge: "Hybrid recommendation",
        color: "hsl(45 75% 65%)",
        href: "https://techops.eigooinc.net",
        icon: Crosshair,
        ctaLabel: "Begin with Tech Ops",
    },
};

function score(answers) {
    let it = 0;
    let sec = 0;
    for (const q of QUESTIONS) {
        const choice = answers[q.id];
        if (choice == null) continue;
        const opt = q.options[choice];
        it += opt.weight.it;
        sec += opt.weight.sec;
    }
    if (sec > it && sec - it >= 2) return "sec";
    if (it > sec && it - sec >= 2) return "it";
    return "tie";
}

export default function SovereignMatch() {
    const [answers, setAnswers] = useState({});
    const completed = QUESTIONS.every((q) => answers[q.id] != null);
    const result = completed ? RESULTS[score(answers)] : null;
    const Icon = result?.icon;

    const reset = () => setAnswers({});

    return (
        <section
            id="sovereign-match"
            className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 relative"
            data-testid="sovereign-match"
        >
            <div className="flex items-baseline justify-between flex-wrap gap-3 mb-10">
                <div>
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-[hsl(45_85%_68%)] mb-3">
                        <Wrench size={12} strokeWidth={1.5} /> Sovereign Match · Scouter
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
                        General tech, or
                        <span className="block gold-gradient-text mt-1 italic font-normal">security specialist?</span>
                    </h2>
                </div>
                <p className="text-sm text-muted-foreground max-w-sm">
                    Three calibration questions. The scouter routes you to the right Tech Ops platform —
                    standard front door or the NDA-gated Vault.
                </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {QUESTIONS.map((q, qi) => (
                    <div key={q.id} className="glass rounded-2xl p-6 border border-[hsl(40_40%_25%/0.25)]">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)] mb-3">
                            Q{qi + 1}
                        </div>
                        <div className="font-heading text-xl tracking-tight mb-5">{q.prompt}</div>
                        <ul className="space-y-2">
                            {q.options.map((opt, oi) => {
                                const selected = answers[q.id] === oi;
                                return (
                                    <li key={oi}>
                                        <button
                                            onClick={() => setAnswers({ ...answers, [q.id]: oi })}
                                            data-testid={`scouter-${q.id}-${oi}`}
                                            className={`w-full text-left px-4 py-3 rounded-md text-sm border transition-colors ${
                                                selected
                                                    ? "border-[hsl(45_85%_68%)] bg-[hsl(40_60%_18%/0.45)] text-foreground"
                                                    : "border-[hsl(40_40%_25%/0.3)] text-muted-foreground hover:border-[hsl(45_85%_68%/0.55)] hover:text-foreground"
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
            </div>

            <div className="mt-10">
                {!result ? (
                    <div className="rounded-2xl border border-dashed border-[hsl(40_40%_30%/0.3)] p-6 text-sm text-muted-foreground" data-testid="scouter-pending">
                        Answer all three to receive a routing recommendation. Or skip ahead — both bridges are linked below.
                    </div>
                ) : (
                    <div
                        className="glass rounded-2xl p-7 border-2 fade-up"
                        style={{ borderColor: `${result.color}` }}
                        data-testid="scouter-result"
                    >
                        <div className="flex items-start gap-4 flex-wrap">
                            <div
                                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                                style={{ backgroundColor: `${result.color}25`, color: result.color }}
                            >
                                <Icon size={20} strokeWidth={1.75} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                                    Sovereign Match recommends
                                </div>
                                <div className="font-heading text-2xl mt-1 tracking-tight">
                                    {result.title}
                                </div>
                                <div className="mt-1 text-[10px] uppercase tracking-[0.25em]" style={{ color: result.color }}>
                                    {result.badge}
                                </div>
                                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                                    {result.copy}
                                </p>
                            </div>
                            <div className="flex flex-col gap-2 items-stretch sm:items-end">
                                <a
                                    href={result.href}
                                    data-testid="scouter-result-cta"
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                                >
                                    {result.ctaLabel}
                                    <ArrowUpRight size={14} />
                                </a>
                                <button
                                    onClick={reset}
                                    data-testid="scouter-reset"
                                    className="inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                                >
                                    <RefreshCw size={11} /> Reset scouter
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Twin-bridge fallback links — always visible so visitors can bypass the scouter */}
            <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
                <a
                    href="https://techops.eigooinc.net"
                    data-testid="scouter-direct-cyberops"
                    className="flex items-center justify-between gap-3 px-5 py-3 rounded-xl border border-[hsl(40_40%_25%/0.3)] hover:border-[hsl(45_85%_68%/0.5)] transition-colors"
                >
                    <span className="flex items-center gap-2.5">
                        <Activity size={16} className="text-[hsl(153_55%_55%)]" />
                        <span className="text-muted-foreground">Skip to <span className="text-foreground font-medium">Tech Ops</span> · general IT</span>
                    </span>
                    <ArrowUpRight size={14} className="text-muted-foreground" />
                </a>
                <a
                    href="https://techopssa.eigooinc.net"
                    data-testid="scouter-direct-vault"
                    className="flex items-center justify-between gap-3 px-5 py-3 rounded-xl border border-[hsl(40_40%_25%/0.3)] hover:border-[hsl(45_85%_68%/0.5)] transition-colors"
                >
                    <span className="flex items-center gap-2.5">
                        <ShieldOff size={16} className="text-[hsl(45_85%_68%)]" />
                        <span className="text-muted-foreground">Skip to <span className="text-foreground font-medium">Tech Ops SA · The Vault</span></span>
                    </span>
                    <ArrowUpRight size={14} className="text-muted-foreground" />
                </a>
            </div>
        </section>
    );
}
